<template>
    <app-layout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                CSV Upload
            </h2>
        </template>

        <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
            <form v-if="permissions.upload.subscribes" @submit.prevent="submitSubscribes">
                <div class="px-4 py-5 bg-white sm:p-6 shadow-xl rounded-tl-md rounded-tr-md">
                    <h2 class="text-lg mb-3">Upload Data</h2>

                    <p>
                        Please upload your data in CSV in the following format.<br>
                        The order of the columns does not matter, but the headers are required.
                    </p>

                    <table class="mt-2 mb-5 w-full border-b">
                        <thead>
                        <tr class="text-left border-b">
                            <th class="p-2 text-xs uppercase font-semibold tracking-wider text-gray-500 w-1">Required</th>
                            <th class="p-2 text-xs uppercase font-semibold tracking-wider text-gray-500">Name</th>
                            <th class="p-2 text-xs uppercase font-semibold tracking-wider text-gray-500">Header</th>
                            <th class="p-2 text-xs uppercase font-semibold tracking-wider text-gray-500">Format</th>
                            <th class="p-2 text-xs uppercase font-semibold tracking-wider text-gray-500">Example</th>
                        </tr>
                        </thead>
                        <tbody class="divide-y text-sm">
                        <tr v-for="column in columns">
                            <td class="p-2" :class="[column.required ? 'text-green-600' : 'text-red-600']">{{ column.required ? 'Yes' : 'No' }}</td>
                            <td class="p-2">{{ column.name }}</td>
                            <td class="p-2">{{ column.header }}</td>
                            <td class="p-2">{{ column.format }}</td>
                            <td class="p-2">{{ column.example }}</td>
                        </tr>
                        </tbody>
                    </table>

                    <validation-errors :errors="subscribeForm.errors" class="mb-4"/>

                    <div v-if="subscribeMessage" class="px-3 py-2 mb-3 rounded bg-green-300 border border-green-700 text-green-900">{{ subscribeMessage }}</div>

                    <div class="mt-3">
                        <jet-label for="file" value="CSV File"/>
                        <input
                            id="subscribes-file"
                            type="file"
                            ref="subscribesFile"
                            @input="subscribeForm.file = $event.target.files[0]"
                            class="p-2 border border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full text-sm"
                        />
                        <div v-if="subscribeForm.errors.file" class="mt-2 text-sm text-red-600">{{ subscribeForm.errors.file }}</div>
                    </div>
                </div>
                <div class="flex items-center justify-end px-4 py-3 bg-gray-50 text-right sm:px-6 shadow-xl sm:rounded-bl-md sm:rounded-br-md">
                    <inertia-link
                        class="rounded-md border border-transparent bg-yellow-400 hover:bg-yellow-300 transition inline-flex items-center px-4 py-2 mr-2 text-xs text-white uppercase tracking-widest font-semibold"
                        :href="route('dashboard')">
                        Cancel
                    </inertia-link>
                    <jet-button :disabled="subscribeForm.processing || !(subscribeForm.file)">Upload Subscribes</jet-button>
                </div>
            </form>

            <form v-if="permissions.upload.unsubscribes" class="mt-8" @submit.prevent="submitUnsubscribes">
                <div class="px-4 py-5 bg-white sm:p-6 shadow-xl rounded-tl-md rounded-tr-md">
                    <h2 class="text-lg mb-3">Upload Unsubscribes</h2>

                    <p class="my-3">Your unsubscribe CSV should only contain an email column, including the header.</p>

                    <validation-errors :errors="unsubscribeForm.errors" class="mb-4"/>

                    <div v-if="unsubscribeMessage" class="px-3 py-2 mb-3 rounded bg-green-300 border border-green-700 text-green-900">{{ unsubscribeMessage }}</div>

                    <div class="mt-3">
                        <jet-label for="file" value="CSV File"/>
                        <input
                            id="unsubscribes-file"
                            type="file"
                            ref="unsubscribesFile"
                            @input="unsubscribeForm.file = $event.target.files[0]"
                            class="p-2 border border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full text-sm"
                        />
                        <div v-if="unsubscribeForm.errors.file" class="mt-2 text-sm text-red-600">{{ unsubscribeForm.errors.file }}</div>
                    </div>
                </div>
                <div class="flex items-center justify-end px-4 py-3 bg-gray-50 text-right sm:px-6 shadow-xl sm:rounded-bl-md sm:rounded-br-md">
                    <inertia-link
                        class="rounded-md border border-transparent bg-yellow-400 hover:bg-yellow-300 transition inline-flex items-center px-4 py-2 mr-2 text-xs text-white uppercase tracking-widest font-semibold"
                        :href="route('dashboard')">
                        Cancel
                    </inertia-link>
                    <jet-button :disabled="unsubscribeForm.processing || !(unsubscribeForm.file)">Upload Unsubscribes</jet-button>
                </div>
            </form>
        </div>
    </app-layout>
</template>

<script>
    import {ref} from 'vue';
    import AppLayout from '@/Layouts/AppLayout';
    import JetLabel from '@/Jetstream/Label';
    import JetInput from '@/Jetstream/Input';
    import JetButton from '@/Jetstream/Button';
    import ValidationErrors from '@/Components/ValidationErrors';
    import {useForm, usePage} from '@inertiajs/inertia-vue3'

    export default {
        components: {
            AppLayout,
            JetLabel,
            JetInput,
            JetButton,
            ValidationErrors,
        },

        props: [
            'verticals',
            'columns',
            'subscribeMessage',
            'unsubscribeMessage',
        ],

        setup() {
            const subscribeForm = useForm({
                _method: 'PUT',
                vertical_id: null,
                file: null,
            });

            const unsubscribeForm = useForm({
                _method: 'PUT',
                vertical_id: null,
                file: null,
            });

            const subscribesFile = ref(null);
            const unsubscribesFile = ref(null);

            function submitSubscribes() {
                subscribeForm.post(route('upload.processSubscribes'), {
                    onFinish: () => {
                        // Reset the file dialog
                        subscribesFile.value.value = null;
                        subscribeForm.reset('file');
                    },
                });
            }

            function submitUnsubscribes() {
                unsubscribeForm.post(route('upload.processUnsubscribes'), {
                    onFinish: () => {
                        // Reset the file dialog
                        unsubscribesFile.value.value = null;
                        unsubscribeForm.reset('file');
                    },
                });
            }

            return {
                subscribeForm,
                unsubscribeForm,
                submitSubscribes,
                submitUnsubscribes,
                subscribesFile,
                unsubscribesFile,
                permissions: usePage().props.value.auth.permissions,
            };
        },
    }
</script>

<style scoped>

</style>
